import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, FormLabel, Input, Text } from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import PropTypes from 'prop-types';
import ModalLayout from '../common/modal/modalLayout';
import { openModal, Modals, closeModal } from '../../redux/reducers';
import { createOrganization, updateOrganization, deleteOrganization } from './api';

const OrganizationForm = ({ organization, changeProp, onDelete }) => {
  const { t } = useTranslation();

  return (
    <Box>
      {onDelete && (
        <Button
          leftIcon={<DeleteIcon />}
          bg="brand.60"
          color="white"
          sx={{ float: 'right' }}
          onClick={onDelete}
        >
          {t('organization.delete')}
        </Button>
      )}
      <Text>{t('interpreter.form.mandatoryFieldInfo')}</Text>
      <FormControl isRequired>
        <FormLabel mt={4}>{t('organization.name')}</FormLabel>
        <Input
          bg="brand.10"
          borderRadius={0}
          defaultValue={organization.name}
          onChange={(e) => changeProp('name', e.target.value)}
          placeholder={t('organization.placeholder')}
        />
      </FormControl>
      <FormControl>
        <FormLabel mt={4}>{t('organization.description')}</FormLabel>
        <Input
          bg="brand.10"
          borderRadius={0}
          defaultValue={organization.description}
          onChange={(e) => changeProp('description', e.target.value)}
          placeholder={t('organization.placeholder')}
        />
      </FormControl>
    </Box>
  );
};

OrganizationForm.propTypes = {
  organization: PropTypes.object.isRequired,
  changeProp: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

const validateOrganization = (organization) => {
  return !!organization.name;
};

const EditOrganizationModal = ({
  createNew,
  organization: initialValue,
  open,
  onUpdate,
  ...rest
}) => {
  const [organization, setOrganization] = useState({});
  const [isValid, setValid] = useState(false);
  useEffect(() => {
    if (open) {
      if (initialValue) {
        // if modal is reopened with same organization, dont clear form fields
        if (initialValue._id != organization._id) {
          setOrganization({ ...initialValue });
          setValid(validateOrganization(initialValue));
        }
      } else {
        if (organization._id) {
          setOrganization({});
          setValid(false);
        }
      }
    }
  }, [open]);

  const changeProp = useCallback(
    (prop, value) => {
      organization[prop] = value;
      setValid(validateOrganization(organization));
    },
    [organization]
  );

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const onClose = useCallback(() => {
    dispatch(closeModal());
  });

  const createOrUpdateOrganization = useCallback(async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      let response;
      if (createNew) {
        response = await createOrganization(organization);
        organization.id = response._id;
      } else {
        response = await updateOrganization(organization);
      }
      onClose();
      onUpdate && onUpdate(response);
    } catch (error) {
      error.createNew = createNew;
      error.organization = organization;
      setSaveError(error);
    } finally {
      setIsSaving(false);
    }
  }, [organization, createNew]);

  useEffect(() => {
    if (saveError != null) {
      const { organization, createNew } = saveError;
      dispatch(
        // Closing error modal should open edit modal again so that user can try saving again without having to edit sessions data again
        openModal(
          {
            headerText: t('modal.error'),
            bodyText: `${t('interpreter.form.updateError')}: ${saveError?.message}`,
            onRightButtonClick: () =>
              dispatch(openModal({ organization, createNew }, Modals.EDIT_ORGANIZATION)),
            onClose: () =>
              dispatch(openModal({ organization, createNew }, Modals.EDIT_ORGANIZATION)),
          },
          Modals.ERROR
        )
      );
    }
  }, [saveError]);

  const canDelete = !createNew && (organization.members || []).length === 0;
  const onDelete = useCallback(async () => {
    setSaveError(null);
    try {
      await deleteOrganization(organization._id);
      onClose();
      organization.deleted = true;
      onUpdate && onUpdate(organization);
    } catch (error) {
      error.createNew = false;
      error.organization = organization;
      setSaveError(error);
    }
  }, [organization]);

  return (
    <ModalLayout
      open={open}
      headerText={t(createNew ? 'organization.create' : 'organization.update')}
      {...rest}
      leftButtonProps={{ bg: null }}
      rightButtonProps={{ color: 'white' }}
      leftButtonText={t('modal.cancel')}
      rightButtonText={t('modal.save')}
      disableRightButton={isSaving || !isValid}
      disableLeftButton={isSaving}
      onLeftButtonClick={onClose}
      onRightButtonClick={createOrUpdateOrganization}
      showProgress={isSaving}
      onClose={onClose}
      size="lg"
    >
      <OrganizationForm
        key={organization._id}
        organization={organization}
        changeProp={changeProp}
        onDelete={canDelete ? onDelete : null}
      />
    </ModalLayout>
  );
};

EditOrganizationModal.propTypes = {
  createNew: PropTypes.bool,
  organization: PropTypes.object,
  open: PropTypes.bool,
  onUpdate: PropTypes.func,
};

EditOrganizationModal.defaultProps = {
  open: false,
};

export default EditOrganizationModal;
